/**
 * After an account is deleted (U-2): sign this device out and show a confirmation screen. The notice is kept
 * in sessionStorage so it survives the reload a stale request's 401 can trigger after the account is gone.
 */
import { ref } from 'vue'
import { supabase } from '@/supabase'
import store from '@/stores/index'
import { useAuthStore } from '@/stores/auth'
import { clearBootCache } from '@/native/bootCache'
import { clearAppBadge } from '@/native/badge'

const NOTICE_KEY = 'lh.accountDeleted'

export type AccountDeletedKind = 'workspace' | 'member'

function readNotice(): AccountDeletedKind | null {
    try {
        const v = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(NOTICE_KEY) : null
        return v === 'workspace' || v === 'member' ? v : null
    } catch {
        return null
    }
}

/** Set while the "account deleted" confirmation should show. */
export const accountDeletedNotice = ref<AccountDeletedKind | null>(readNotice())

export function dismissAccountDeletedNotice(): void {
    accountDeletedNotice.value = null
    try {
        sessionStorage.removeItem(NOTICE_KEY)
    } catch {
        /* ignore */
    }
}

/** The server already removed the account: clear local state and show the confirmation. */
export async function finishAccountDeletion(kind: AccountDeletedKind): Promise<void> {
    accountDeletedNotice.value = kind
    try {
        sessionStorage.setItem(NOTICE_KEY, kind)
    } catch {
        /* ignore */
    }
    try {
        const { clearInboxCache } = await import('@/composables/inbox/inboxCache')
        await clearInboxCache()
    } catch {
        /* best effort */
    }
    clearBootCache()
    void clearAppBadge()
    // Local only: the server session went with the account.
    try {
        await supabase.auth.signOut({ scope: 'local' })
    } catch {
        /* ignore */
    }
    try {
        useAuthStore().clearUser()
        store.clearLocalStorage()
    } catch {
        /* ignore */
    }
}

export const ACCOUNT_DELETED_COPY: Record<AccountDeletedKind, { title: string; body: string }> = {
    workspace: {
        title: 'Your account has been deleted',
        body: 'Your Lighthouse workspace and its data have been permanently deleted.',
    },
    member: {
        title: 'Your account has been deleted',
        body: 'Your Lighthouse account and your personal data have been permanently deleted. Your workspace is unchanged for everyone else.',
    },
}
